/**
 * Pricing / tax helpers.
 *
 * Mowilex's Epicor price list is VAT-inclusive — the price the
 * customer sees IS the price they pay. The portal must NOT add a
 * separate PPN line or inflate the total by 11%; the legally-signed
 * DMS invoice from Zegen breaks PPN out for tax compliance, but for
 * the customer-facing cart / checkout / order detail the total
 * equals the post-discount subtotal.
 *
 * Breakdown rendered everywhere:
 *
 *   Total Selling Price  (original, pre-discount subtotal)
 *   Discount (Rp)        (discount amount, 0 if none)
 *   Subtotal             (Total Selling Price − Discount)
 *   Total                (= Subtotal — VAT already in the price)
 *
 * The backend `OrderResponse` still ships `tax_amount` and
 * `grand_total_amount` for backwards compatibility, but they are now
 * 0 and `subtotal_amount` respectively. `computeBreakdown` is a
 * fallback for code paths where only a raw subtotal and optional
 * discount are on hand (e.g. the cart before an order is created).
 */

/** Indonesian VAT (PPN) rate. Exported for legacy callers; we no
 *  longer add VAT on top of subtotals because Mowilex's price list
 *  is VAT-inclusive. The DMS / Zegen invoice still surfaces the PPN
 *  portion broken out for faktur-pajak compliance. */
export const TAX_RATE = 0.0;

export interface PriceBreakdown {
  /** Total Selling Price — the original subtotal before any discount. */
  originalSubtotal: number;
  /** Total discount in IDR (positive number). */
  discount: number;
  /** Subtotal after discount but before tax. */
  subtotal: number;
  /** PPN (tax amount). */
  tax: number;
  /** Grand total: subtotal + tax. */
  total: number;
}

/**
 * Build a 4-line price breakdown from a raw subtotal and an optional
 * discount amount. Safe for the cart page where no backend order
 * object exists yet.
 */
export function computeBreakdown(
  originalSubtotal: number,
  discount: number = 0,
): PriceBreakdown {
  const orig = Math.max(0, originalSubtotal || 0);
  const disc = Math.max(0, discount || 0);
  const subtotal = Math.max(0, orig - disc);
  // Price list is VAT-inclusive — no PPN added on top. The DMS
  // invoice still shows PPN broken out for compliance.
  const tax = 0;
  const total = subtotal;
  return {
    originalSubtotal: orig,
    discount: disc,
    subtotal,
    tax,
    total,
  };
}

/**
 * Format a number as IDR currency (no decimal places, dot thousand
 * separator — matches the target screenshot and Epicor invoices).
 */
export function formatIDR(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return 'Rp 0';
  return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
}
